import React, { useEffect, useState } from 'react'

import { Modal, PrimaryButton, BodyText } from '@hm-group/fabric-components'
import { ClickableDiscountBanner } from '@pb/components'
import { getLabelFromStorage, getLocale } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import { setLastAppliedUnavailableVoucher } from 'features/vouchers/duck/actions'
import {
  operationClearDiscountError,
  operationGetVouchers,
  operationGetConfiguredOffers,
  operationClearDeletedDiscounts,
  operationsToggleVoucher,
} from 'features/vouchers/duck/operations'
import selectors from 'features/vouchers/duck/selectors'
import { trackOptimizelyEvent } from 'helpers/optimizely'

import { VOUCHERS_LOCATION } from '../../constants'
import Discounts from '../Discounts'
import { redeemVoucher, closeVoucherBanner } from '../Discounts/helpers/tracking'
import Offers from '../Offers'
import OfferLogin from '../Offers/OfferLogin'
import VouchersModalTrigger from '../VouchersModalTrigger'

export interface VouchersOverviewProps {
  location?: string
  isAppliedVoucherBannerEnabled?: boolean
  appliedVoucherDisclaimer?: string
  appliedVoucherModalHeading?: string
  bannerText?: string
  offerKey?: string
}

const VouchersOverview: React.FC<VouchersOverviewProps> = ({
  location,
  isAppliedVoucherBannerEnabled,
  appliedVoucherDisclaimer,
  appliedVoucherModalHeading,
  bannerText,
  offerKey,
}) => {
  const dispatch = useDispatch()
  const locale = getLocale()
  const [{ enabled: isWOBannerActive }] = useDecision(OPTIMIZELY.LCA_WELCOME_OFFER_FEATURE)
  const [{ enabled: isVoucherActive }] = useDecision(OPTIMIZELY.LCA_VOUCHER_SHOPPING_BAG)
  const [{ variables: oneClickContent }] = useDecision(OPTIMIZELY.LCA_VOUCHER_SHOPPING_BAG)
  const [{ variables: multiClickContent }] = useDecision(OPTIMIZELY.LCA_MULTI_CLICK_BANNER)
  const [{ enabled: isMultiClickBannerActive }] = useDecision(OPTIMIZELY.LCA_MULTI_CLICK_BANNER)
  const multiClickContentVar = multiClickContent && (multiClickContent as any)
  const variablesContent = oneClickContent && (oneClickContent.voucher_content as Object)
  const isCart = location === VOUCHERS_LOCATION.cart
  const availableOffers = useSelector(selectors.getOffers)
  const appliedVouchersCount = useSelector(selectors.getAppliedVouchersCount)

  const appliedOffers = useSelector(selectors.getAppliedOffers)
  const isSignedIn = useSelector(appSelectors.getIsSignedIn)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isReedemClicked, setIsRedeemClicked] = useState(false)
  const modalTitle = isAppliedVoucherBannerEnabled
    ? appliedVoucherModalHeading
    : getLabelFromStorage('checkout.discounts.title')

  useEffect(() => {
    if (!isModalOpen) {
      dispatch(operationClearDiscountError())
      dispatch(operationClearDeletedDiscounts())
    }

    if (isSignedIn && !availableOffers.length) {
      dispatch(operationGetConfiguredOffers())
      dispatch(operationGetVouchers())
    }

    if (isModalOpen) {
      dispatch(setLastAppliedUnavailableVoucher(undefined))
    }

    if (isVoucherActive) {
      trackOptimizelyEvent(OPTIMIZELY.VIEW_BANNER_REMINDER)
    }
    if (isMultiClickBannerActive) {
      trackOptimizelyEvent(OPTIMIZELY.VIEW_BANNER)
    }
  }, [isModalOpen])

  const toggleModal = (): void => {
    setIsModalOpen(!isModalOpen)
    if (isModalOpen && isWOBannerActive) {
      const offerToSend = appliedOffers.map((offer) => `${offer.offerKey} - ${offer.name})`)

      redeemVoucher(offerToSend)
    }
    if (isVoucherActive || isWOBannerActive || isMultiClickBannerActive) {
      trackOptimizelyEvent(OPTIMIZELY.REDEEM_VOUCHER)
      trackOptimizelyEvent(OPTIMIZELY.TOTAL_REDEEM)
      trackOptimizelyEvent(OPTIMIZELY.CTA_REDEEM)
      trackOptimizelyEvent(OPTIMIZELY.WELCOME_OFFER_ADDED_TO_CART)
    }
    trackOptimizelyEvent(OPTIMIZELY.USED_DISCOUNT_CODE)
  }

  const redeemClick = (): void => {
    dispatch(operationsToggleVoucher(availableOffers[0]))
    setIsRedeemClicked(!isReedemClicked)

    if (isReedemClicked) {
      trackOptimizelyEvent(OPTIMIZELY.ADD_VOUCHER_BANNER)
      trackOptimizelyEvent(OPTIMIZELY.TOTAL_REDEEM)
    } else {
      trackOptimizelyEvent(OPTIMIZELY.CTA_UNDO)
    }
  }

  const closeModal = (): void => {
    if (isWOBannerActive) {
      closeVoucherBanner()
    }
    setIsModalOpen(!isModalOpen)
  }

  const isRedeemDisabled = appliedVouchersCount === 0

  return process.env.IS_VOUCHER_MODAL_ENABLED === 'enabled' ? (
    <>
      {availableOffers.length === 1 &&
        isMultiClickBannerActive &&
        availableOffers.map((item) => {
          return (
            <ClickableDiscountBanner
              title={getLabelFromStorage(
                `${multiClickContentVar.multiClickContent[locale].promotion_text}`,
              )}
              haveSubtitle
              ctaEvent={redeemClick}
              subtitle={getLabelFromStorage('hub.expires.at', {
                replacements: item.personalizedExpireDate,
              })}
              isReedemClicked={isReedemClicked}
              ctaText={getLabelFromStorage(
                `${multiClickContentVar.multiClickContent[locale].redeem_cta}`,
              )}
              ctaUndo={getLabelFromStorage(
                `${multiClickContentVar.multiClickContent[locale].undo_cta}`,
              )}
            />
          )
        })}
      {isMultiClickBannerActive && availableOffers.length > 1 && (
        <ClickableDiscountBanner
          title={getLabelFromStorage(
            `${availableOffers.length} ${multiClickContentVar.multiClickContent[locale].banner_title}`,
          )}
          haveSubtitle
          subtitle={`${multiClickContentVar.multiClickContent[locale].banner_description}`}
          isMoreThanOneVoucherPresent
          isReedemClicked={isReedemClicked}
          ctaEvent={toggleModal}
          ctaText=""
          ctaUndo=""
        />
      )}
      {isVoucherActive &&
        !isMultiClickBannerActive &&
        availableOffers?.map((item) => {
          if (
            getLabelFromStorage(`${variablesContent[locale].welcome_offer_key}`) === item.offerKey
          ) {
            return (
              <ClickableDiscountBanner
                title={getLabelFromStorage('sboneclick.single.welcomeoffer.title')}
                haveSubtitle
                ctaEvent={redeemClick}
                subtitle={getLabelFromStorage('hub.expires.at', {
                  replacements: item.personalizedExpireDate,
                })}
                isReedemClicked={isReedemClicked}
                ctaText="sboneclick.single.cta.redeem"
                ctaUndo="sboneclick.single.cta.undo"
              />
            )
          }
          return <></>
        })}
      <VouchersModalTrigger
        bannerText={bannerText}
        offerKey={offerKey}
        toggleModal={toggleModal}
        location={location}
        isAppliedVoucherBannerEnabled={isAppliedVoucherBannerEnabled}
      />
      <Modal
        closeLabel={getLabelFromStorage('general.close')}
        heading={modalTitle || ''}
        isOpen={isModalOpen}
        onClose={closeModal}
        content={
          <>
            {isAppliedVoucherBannerEnabled && <BodyText>{appliedVoucherDisclaimer}</BodyText>}
            <Discounts location={location} />
            {isCart && <OfferLogin inlineLogin />}
            {process.env.IS_OFFERS_ENABLED === 'enabled' && <Offers location={location} />}
          </>
        }
        actions={
          <PrimaryButton onClick={toggleModal} isDisabled={isRedeemDisabled} hasFullWidth>
            {getLabelFromStorage('discount.apply.text')}
          </PrimaryButton>
        }
      />
    </>
  ) : (
    <Discounts location={location} />
  )
}

export default VouchersOverview
